'use client';

import ExplainDialog from '@/app/[variants]/_lens-components/components/ExplainDialog';
import LensIndexPrice from '@/app/[variants]/_lens-components/components/LensIndexPrice';
import { useLensProcessStore } from '@/app/[variants]/_lens-components/store/lens-process-store';
import stepCommonClassName from '@/app/[variants]/_lens-components/style/step-common';
import { LensIndexItem } from '@/app/[variants]/_lens-components/types';
import {
  getApiPrescriptionType,
  handleColorItemPriceInfo,
  handleLensIndexPriceInfo,
} from '@/app/[variants]/_lens-components/utils';
import QuestionResponsive from '@/components/IconResponsive/QuestionResponsive';
import Image from '@/components/Image';
import Tooltip from '@/components/Tooltip';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { processLineBreaks } from '@/utils/tools';
import { useMediaQuery } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useEffect, useState } from 'react';

export default function LensIndex({
  lensIndexTip,
  selectedLensIndexItem,
  onSelectedLensIndexItem,
}: {
  lensIndexTip: string;
  selectedLensIndexItem: LensIndexItem;
  onSelectedLensIndexItem: (lensIndexItem: LensIndexItem) => void;
}) {
  const t = useTranslations('lens-process');
  const {
    lensGoodsSensorsData,
    prescriptionType,
    presTypeOptionName,
    selectedLensType,
    selectedBrand,
    selectedColorItem,
    hasIndexPrice,
    lensTypeForTracking,
  } = useLensProcessStore();
  const { trackEvent } = useReporting();
  const lensIndexList = selectedColorItem?.lens_list || [];
  const [showExplainPop, setShowExplainPop] = useState(false);
  const [explainContent, setExplainContent] = useState<{
    index_name?: string;
    index_explain?: string;
  }>({});
  const userInfo = useStore((state) => state.userInfo);
  const currency = useStore((state) => state.currency);
  const waiMode = useStore((state) => state.waiMode);
  const isWap = useMediaQuery('(max-width:1280px)');
  useEffect(() => {
    // 埋点：页面浏览
    try {
      const colorItemPriceInfo = handleColorItemPriceInfo(
        selectedColorItem,
        false,
        currency,
        userInfo,
      );
      trackEvent(
        {
          eventName: 'LensIndexPage_View',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type_name: presTypeOptionName,
            prescription_type: getApiPrescriptionType(prescriptionType),
            lens_type_name: selectedLensType?.lens_name,
            lens_type: lensTypeForTracking,
            lens_brand: selectedBrand?.brand_title,
            lens_brand_name: selectedBrand?.brand_name,
            lens_color: selectedColorItem?.color_name,
            lens_price: parseFloat(colorItemPriceInfo.price),
            current_lens_price: parseFloat(colorItemPriceInfo.sell_price),
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }, [userInfo]);

  // 处理描述信息
  const handleDes = (item: LensIndexItem) => {
    const { brand_description = '' } = item;
    const brandArr = brand_description
      ? brand_description.replace(/\\n|\n/g, '/n@#@').split('/n@#@')
      : [];

    return brandArr.map((text: string) => ({
      text,
      premium: 1,
    }));
  };

  // 是否显示高级标签
  const showPremium = (item: LensIndexItem) => {
    return item.index_brand && item.index_brand === 'pro';
  };

  // 显示说明弹窗
  const showExplain = (item: LensIndexItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setExplainContent(item);
    setShowExplainPop(true);
  };

  // 选择折射率
  const onItemClick = (item: LensIndexItem) => {
    // 如果选中的和已选中的一样，不做操作
    if (selectedLensIndexItem && item.id === selectedLensIndexItem.id) {
      return;
    }

    onSelectedLensIndexItem(item);

    // 这里可以添加埋点逻辑
    SDLensIndexPageSelectClick(item);
  };

  function SDLensIndexPageSelectClick(lensIndexItem: LensIndexItem) {
    try {
      const lensIndexPriceInfo = handleLensIndexPriceInfo(lensIndexItem, currency, userInfo);
      const colorItemPriceInfo = handleColorItemPriceInfo(
        selectedColorItem,
        hasIndexPrice,
        currency,
        userInfo,
      );
      trackEvent(
        {
          eventName: 'LensIndexPage_Select_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            lens_id: `${lensIndexItem.id}`,
            lens_price: parseFloat(colorItemPriceInfo.price),
            current_lens_price: parseFloat(colorItemPriceInfo.sell_price),
            lens_index_name: lensIndexItem.index_name,
            lens_index: lensIndexItem.refractive_index,
            lens_code: lensIndexItem.code,
            lens_index_price: parseFloat(lensIndexPriceInfo.price),
            current_lens_index_price: parseFloat(lensIndexPriceInfo.sell_price),
            currency: currency.name,

            prescription_type_name: presTypeOptionName,
            prescription_type: getApiPrescriptionType(prescriptionType),
            lens_type_name: selectedLensType?.lens_name,
            lens_type: lensTypeForTracking,
            lens_brand: selectedBrand?.brand_title,
            lens_brand_name: selectedBrand?.brand_name,
            lens_color: selectedColorItem?.color_name,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }

  return (
    <div className="font-br h-full box-border overflow-y-hidden">
      <div className="overflow-auto h-full">
        <h3 className={stepCommonClassName.stepTitle}>
          <span>{t('lens')}</span>
        </h3>

        {lensIndexTip && (
          <p className="max-xl:mx-[14px] mb-[9px] xl:mb-[13px] p-[6px_10px] xl:p-[12px_14px] leading-[16px] text-[12px] xl:text-[14px] font-br bg-[#fff9d7] rounded-[6px]">
            {lensIndexTip}
          </p>
        )}

        {Array.isArray(lensIndexList) && lensIndexList.length > 0 ? (
          <ul className={stepCommonClassName.optionsContainer}>
            {lensIndexList.map((item: any, index: number) => (
              <li
                key={index}
                className={`${stepCommonClassName.optionItem} ${
                  item.recommend === 1 ? stepCommonClassName.recommendedItem : ''
                }`}
              >
                {item.recommend === 1 && (
                  <div className={stepCommonClassName.recommendBadge}>
                    <div className={stepCommonClassName.recommendBadgeContent}>
                      <span>{t('recommendedForYou')}</span>
                    </div>
                  </div>
                )}

                <div
                  className={`${stepCommonClassName.optionBox} ${
                    selectedLensIndexItem && selectedLensIndexItem.id === item.id
                      ? stepCommonClassName.selectedOptionBox
                      : ''
                  }`}
                  onClick={() => onItemClick(item)}
                >
                  <div className={stepCommonClassName.optionContent}>
                    <div className={`${stepCommonClassName.imageContainer} min-w-[60px]`}>
                      <Image
                        className="w-[60px] h-[60px]"
                        src={item.index_img || ''}
                        alt={item.index_name || ''}
                        width={60}
                        height={60}
                      />
                    </div>

                    <div className={`${stepCommonClassName.textContainer} mt-[7px]`}>
                      <div className={stepCommonClassName.textCenterContainer}>
                        <div className={stepCommonClassName.titleContainer}>
                          <p>{item.index_name}</p>
                          {showPremium(item) && (
                            <div className="ml-[3px] xl:ml-[6px] p-[0_9px] h-[13px] xl:h-[16px] bg-[#f2ddbc] rounded-[8px] font-br text-[#3a2107] text-[12px] flex items-center justify-center">
                              <span className="relative top-[1px]">{t('premium')}</span>
                            </div>
                          )}
                          {item.index_explain && (
                            <Tooltip
                              title={
                                <p
                                  className="text-[14px]"
                                  dangerouslySetInnerHTML={{
                                    __html: processLineBreaks(item.index_explain),
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                />
                              }
                              placement="bottom"
                              variant="light"
                              sx={{
                                zIndex: 1500,
                                '& .MuiTooltip-tooltip': {
                                  fontSize: '14px',
                                  maxWidth: '490px',
                                },
                              }}
                            >
                              <QuestionResponsive
                                className={stepCommonClassName.questionIcon}
                                onClick={(e) => {
                                  isWap && showExplain(item, e);
                                  e.stopPropagation();
                                }}
                              />
                            </Tooltip>
                          )}
                        </div>

                        <div className={`${stepCommonClassName.description(waiMode)}`}>
                          {handleDes(item).map(
                            (desItem: { text: string; premium: number }, desIndex: number) => (
                              <p
                                key={desIndex}
                                className="break-all mb-[10px] leading-[1] flex items-center justify-start mt-[16px]"
                              >
                                <span
                                  className={`inline-block w-[4px] h-[4px] ${waiMode ? 'bg-[#282828]' : 'bg-[grey]'} rounded-[50%] mr-[6px]`}
                                />
                                <span className={`${waiMode ? '' : 'text-[grey]'}`}>
                                  {desItem.text}
                                </span>
                              </p>
                            ),
                          )}
                        </div>
                      </div>

                      <div className={`${stepCommonClassName.priceContainer}`}>
                        <LensIndexPrice
                          renderPrice={(price, sellPrice) => (
                            <span className={stepCommonClassName.priceInnerBox}>
                              {Number(sellPrice?.replace(/[^0-9.]/g, '')) > 0 ? (
                                <span className={stepCommonClassName.priceText}>{sellPrice}</span>
                              ) : (
                                <span className={stepCommonClassName.priceText}>{t('free')}</span>
                              )}
                              {price !== sellPrice && (
                                <span
                                  className={`${stepCommonClassName.originalPriceText(waiMode)}`}
                                >
                                  {price}
                                </span>
                              )}
                            </span>
                          )}
                          currency={currency}
                          currencyName={currency.name}
                          lensIndexItem={item}
                        />

                        <LensIndexPrice
                          renderPrice={(price, sellPrice, discountPercent) => {
                            if (discountPercent > 0) {
                              return (
                                <div
                                  className={`absolute top-0 right-0 h-[15px] p-[0px_6px] ${(() => {
                                    if (userInfo.is_member !== 1) return 'bg-[#fca6ff]';

                                    switch (userInfo.member_level) {
                                      case 0:
                                        return 'bg-[#f0dbd8]';
                                      case 1:
                                        return 'bg-[#d8e3f0]';
                                      case 2:
                                        return 'bg-[#fcddb6]';
                                      case 3:
                                        return 'bg-gradient-to-r from-[#e4dfff] to-[#bcbcff]';
                                      default:
                                        return 'bg-[#f0dbd8]';
                                    }
                                  })()} rounded-[0px_4px_0px_6px] text-[11px] font-br flex items-center justify-center`}
                                >
                                  <span className="relative top-[1px]">
                                    {t('discountPercentOff', { discountPercent })}
                                  </span>
                                </div>
                              );
                            }
                            return null;
                          }}
                          currency={currency}
                          currencyName={currency.name}
                          lensIndexItem={item}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className={stepCommonClassName.emptyState}>
            <Image
              src="/static-page/null.png"
              alt={t('noLensesAvailable')}
              width={118}
              height={118}
            />
            <p className={stepCommonClassName.emptyStateText}>
              {t('sorryNoLensesAvailableForYourPrescription')}
            </p>
            <button className={stepCommonClassName.emptyStateButton}>
              {t('editPrescription')}
            </button>
          </div>
        )}
      </div>

      {/* 镜片说明弹窗 */}
      <ExplainDialog open={showExplainPop} onClose={() => setShowExplainPop(false)}>
        <div>
          <h3 className="text-primary-black text-center text-[16px] mb-[16px] font-normal font-bs">
            {explainContent?.index_name}
          </h3>
          <p
            className="text-[13px] leading-[24px] font-br"
            dangerouslySetInnerHTML={{ __html: processLineBreaks(explainContent?.index_explain) }}
          />
        </div>
      </ExplainDialog>
    </div>
  );
}
